/**
 * sl-yphr: useWorkspaceEricaSettings Hook
 * Manages Workspace ELSTER Configuration via REST API
 *
 * Endpoints:
 * - GET    /api/workspace/:workspaceId/erica-config       - Get config (masked)
 * - PUT    /api/workspace/:workspaceId/erica-config       - Set/update config
 * - DELETE /api/workspace/:workspaceId/erica-config       - Delete config
 * - PATCH  /api/workspace/:workspaceId/erica-config       - Toggle enabled
 * - POST   /api/workspace/:workspaceId/erica-config/test  - Test connection
 */

import { notify } from '@affine/component';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { useService } from '@toeverything/infra';
import { useCallback, useEffect, useState } from 'react';

import { requestWorkspaceSetting } from '../../shared/workspace-settings-api';

export interface EricaConfigInfo {
  configured: boolean;
  enabled?: boolean;
  testMode?: boolean;
  steuernummer?: string | null;
  bundesland?: string | null;
  buFaNr?: string | null;
  lastVerifiedAt?: Date;
  hasCert?: boolean;
  elsterDocId?: string | null;
}

export interface EricaConnectionTestResult {
  success: boolean;
  error?: string;
}

interface UseWorkspaceEricaSettingsReturn {
  isLoading: boolean;
  isSaving: boolean;
  isTesting: boolean;
  isUploadingCert: boolean;
  config: EricaConfigInfo | null;
  saveConfig: (
    steuernummer?: string,
    bundesland?: string,
    buFaNr?: string
  ) => Promise<void>;
  deleteConfig: () => Promise<void>;
  toggleEnabled: (enabled: boolean) => Promise<void>;
  toggleTestMode: (testMode: boolean) => Promise<void>;
  testConnection: () => Promise<EricaConnectionTestResult>;
  uploadCert: (file: File, pin: string) => Promise<void>;
  deleteCert: () => Promise<void>;
  provisionDoc: () => Promise<string | null>;
  assignDoc: (docId: string) => Promise<string | null>;
  transferDoc: (targetDocId: string) => Promise<string | null>;
  isProvisioning: boolean;
  refresh: () => Promise<void>;
}

export function useWorkspaceEricaSettings(): UseWorkspaceEricaSettingsReturn {
  const workspace = useService(WorkspaceService).workspace;
  const workspaceId = workspace.id;
  const apiBase = `/api/workspace/${workspaceId}/erica-config`;
  const requestErica = useCallback(
    (path: string, init?: RequestInit) =>
      requestWorkspaceSetting(workspaceId, path, init),
    [workspaceId]
  );

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [isUploadingCert, setIsUploadingCert] = useState(false);
  const [isProvisioning, setIsProvisioning] = useState(false);
  const [config, setConfig] = useState<EricaConfigInfo | null>(null);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await requestErica(apiBase);
      if (res.ok) {
        const text = await res.text();
        const data = text ? JSON.parse(text) : { configured: false };
        setConfig(data);
      } else {
        setConfig({ configured: false });
      }
    } catch {
      setConfig({ configured: false });
    } finally {
      setIsLoading(false);
    }
  }, [apiBase, requestErica]);

  useEffect(() => {
    refresh().catch(console.error);
  }, [refresh]);

  const saveConfig = useCallback(
    async (steuernummer?: string, bundesland?: string, buFaNr?: string) => {
      setIsSaving(true);
      try {
        const res = await requestErica(apiBase, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ steuernummer, bundesland, buFaNr }),
        });

        if (!res.ok) {
          const error = await res
            .json()
            .catch(() => ({ message: 'Unknown error' }));
          throw new Error(error.message || 'Failed to save config');
        }

        await refresh();
        notify.success({ title: 'ELSTER configuration saved' });
      } catch (error) {
        console.error('Failed to save ELSTER config:', error);
        notify.error({
          title:
            error instanceof Error ? error.message : 'Failed to save config',
        });
        throw error;
      } finally {
        setIsSaving(false);
      }
    },
    [apiBase, refresh, requestErica]
  );

  const toggleEnabled = useCallback(
    async (enabled: boolean) => {
      setIsSaving(true);
      try {
        const res = await requestErica(apiBase, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ enabled }),
        });

        if (!res.ok) {
          const error = await res
            .json()
            .catch(() => ({ message: 'Unknown error' }));
          throw new Error(error.message || 'Failed to toggle integration');
        }

        await refresh();
        notify.success({
          title: enabled
            ? 'ELSTER integration enabled'
            : 'ELSTER integration disabled',
        });
      } catch (error) {
        console.error('Failed to toggle ELSTER integration:', error);
        notify.error({
          title:
            error instanceof Error
              ? error.message
              : 'Failed to toggle integration',
        });
        throw error;
      } finally {
        setIsSaving(false);
      }
    },
    [apiBase, refresh, requestErica]
  );

  const toggleTestMode = useCallback(
    async (testMode: boolean) => {
      setIsSaving(true);
      try {
        const res = await requestErica(`${apiBase}/test-mode`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ testMode }),
        });

        if (!res.ok) {
          const error = await res
            .json()
            .catch(() => ({ message: 'Unknown error' }));
          throw new Error(error.message || 'Failed to toggle test mode');
        }

        await refresh();
        notify.success({
          title: testMode ? 'Test mode enabled' : 'Production mode enabled',
        });
      } catch (error) {
        console.error('Failed to toggle ELSTER test mode:', error);
        notify.error({
          title:
            error instanceof Error
              ? error.message
              : 'Failed to toggle test mode',
        });
        throw error;
      } finally {
        setIsSaving(false);
      }
    },
    [apiBase, refresh, requestErica]
  );

  const deleteConfig = useCallback(async () => {
    setIsSaving(true);
    try {
      const res = await requestErica(apiBase, {
        method: 'DELETE',
      });

      if (!res.ok) throw new Error('Failed to delete config');

      setConfig({ configured: false });
      notify.success({ title: 'ELSTER configuration removed' });
    } catch (error) {
      console.error('Failed to delete ELSTER config:', error);
      notify.error({ title: 'Failed to remove configuration' });
      throw error;
    } finally {
      setIsSaving(false);
    }
  }, [apiBase, requestErica]);

  const testConnection =
    useCallback(async (): Promise<EricaConnectionTestResult> => {
      setIsTesting(true);
      try {
        const res = await requestErica(`${apiBase}/test`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        });

        const data = await res.json();
        const result: EricaConnectionTestResult = {
          success: data.connected ?? false,
          error: data.error,
        };

        if (result.success) {
          notify.success({ title: 'ELSTER connection successful' });
        } else {
          notify.error({
            title: 'Connection failed',
            message: result.error,
          });
        }

        return result;
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Connection failed';
        notify.error({
          title: 'Connection test failed',
          message: errorMessage,
        });
        return { success: false, error: errorMessage };
      } finally {
        setIsTesting(false);
      }
    }, [apiBase, requestErica]);

  const uploadCert = useCallback(
    async (file: File, pin: string) => {
      setIsUploadingCert(true);
      try {
        const formData = new FormData();
        formData.append('cert', file);
        formData.append('pin', pin);

        const res = await requestErica(`${apiBase}/cert`, {
          method: 'PUT',
          body: formData,
        });

        if (!res.ok) {
          const error = await res
            .json()
            .catch(() => ({ message: 'Unknown error' }));
          throw new Error(error.message || 'Failed to upload certificate');
        }

        await refresh();
        notify.success({ title: 'ELSTER certificate uploaded' });
      } catch (error) {
        console.error('Failed to upload ELSTER certificate:', error);
        notify.error({
          title:
            error instanceof Error
              ? error.message
              : 'Failed to upload certificate',
        });
        throw error;
      } finally {
        setIsUploadingCert(false);
      }
    },
    [apiBase, refresh, requestErica]
  );

  const provisionDoc = useCallback(async (): Promise<string | null> => {
    setIsProvisioning(true);
    try {
      const res = await requestErica(`${apiBase}/provision-doc`, {
        method: 'POST',
      });

      if (!res.ok) {
        const error = await res
          .json()
          .catch(() => ({ message: 'Unknown error' }));
        throw new Error(error.message || 'Failed to provision ELSTER document');
      }

      const data = await res.json();
      await refresh();
      notify.success({ title: 'ELSTER document created' });
      return data.docId as string;
    } catch (error) {
      console.error('Failed to provision ELSTER document:', error);
      notify.error({
        title:
          error instanceof Error
            ? error.message
            : 'Failed to create ELSTER document',
      });
      return null;
    } finally {
      setIsProvisioning(false);
    }
  }, [apiBase, refresh, requestErica]);

  const assignDoc = useCallback(
    async (docId: string): Promise<string | null> => {
      setIsProvisioning(true);
      try {
        const res = await requestErica(`${apiBase}/assign-doc`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ docId }),
        });

        if (!res.ok) {
          const error = await res
            .json()
            .catch(() => ({ message: 'Unknown error' }));
          throw new Error(error.message || 'Failed to assign ELSTER document');
        }

        const data = await res.json();
        await refresh();
        notify.success({ title: 'ELSTER document assigned' });
        return data.docId as string;
      } catch (error) {
        console.error('Failed to assign ELSTER document:', error);
        notify.error({
          title:
            error instanceof Error
              ? error.message
              : 'Failed to assign ELSTER document',
        });
        return null;
      } finally {
        setIsProvisioning(false);
      }
    },
    [apiBase, refresh, requestErica]
  );

  const transferDoc = useCallback(
    async (targetDocId: string): Promise<string | null> => {
      setIsProvisioning(true);
      try {
        const res = await requestErica(`${apiBase}/transfer-doc`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ targetDocId }),
        });

        if (!res.ok) {
          const error = await res
            .json()
            .catch(() => ({ message: 'Unknown error' }));
          throw new Error(
            error.message || 'Failed to transfer ELSTER document'
          );
        }

        const data = await res.json();
        await refresh();
        notify.success({ title: 'ELSTER document transferred' });
        return data.docId as string;
      } catch (error) {
        console.error('Failed to transfer ELSTER document:', error);
        notify.error({
          title:
            error instanceof Error
              ? error.message
              : 'Failed to transfer ELSTER document',
        });
        return null;
      } finally {
        setIsProvisioning(false);
      }
    },
    [apiBase, refresh, requestErica]
  );

  const deleteCert = useCallback(async () => {
    setIsUploadingCert(true);
    try {
      const res = await requestErica(`${apiBase}/cert`, {
        method: 'DELETE',
      });

      if (!res.ok) throw new Error('Failed to delete certificate');

      await refresh();
      notify.success({ title: 'ELSTER certificate removed' });
    } catch (error) {
      console.error('Failed to delete ELSTER certificate:', error);
      notify.error({ title: 'Failed to remove certificate' });
      throw error;
    } finally {
      setIsUploadingCert(false);
    }
  }, [apiBase, refresh, requestErica]);

  return {
    isLoading,
    isSaving,
    isTesting,
    isUploadingCert,
    config,
    saveConfig,
    deleteConfig,
    toggleEnabled,
    toggleTestMode,
    testConnection,
    uploadCert,
    deleteCert,
    provisionDoc,
    assignDoc,
    transferDoc,
    isProvisioning,
    refresh,
  };
}
